/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-redeclare */
import {
  AccountType,
  type ClientSignupDetails,
  ContactType,
  CountryCode,
  type DateTime,
  PhoneNumber,
  StatusResult,
  UserRight,
  UserStatus,
  buildEmailChangeCallbackUrl,
  getCountryCodeAndNameOfClient,
  getSessionRetryDelayMs,
  resolveSessionQuery,
  SessionQueryResolution,
  todayDateTime,
} from '@rentguru/commons-utils';
import {
  createAuthTokenCache,
  graphqlClient,
  onSessionExpiredGraphqlRequest,
  onUnauthorizedGraphqlRequest,
  reconnectSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import React, { Reducer, useEffect, useReducer, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { AuthDenialOutcome, getAuthDenialOutcome } from 'src/auth/authTokenUtils';
import {
  acceptInvitation,
  changeEmail,
  changePassword,
  createOrganization,
  disableTwoFactorRequest,
  enableTwoFactorRequest,
  fetchMyPendingInvitations,
  getFullOrganization,
  getAuthClientToken,
  getSession,
  linkSocialAccount,
  listLinkedAccounts,
  listOrganizations,
  type PendingInvitationForCurrentUser,
  PhoneVerificationError,
  requestPasswordReset,
  resetPassword,
  sendPhoneVerificationOtp,
  sendVerificationEmail,
  setActiveOrganization,
  setPassword,
  signInWithEmail,
  signOut,
  signUpWithEmail,
  stopImpersonating as stopImpersonationRequest,
  toDateTime,
  unlinkSocialAccount,
  updateUser,
  useActiveOrganization,
  useSession,
  verifyBackupCode,
  verifyPhoneNumberOtp,
  verifyTotp,
} from 'src/auth/client';
import { getLinkedProviders, getProviderAccountIds } from 'src/auth/linkedAccountsUtils';
import { SocialProvider } from 'src/auth/socialProviders';
import RouteDestination from 'src/components/Routes/RouteDestination';
import { beginSignOut, endSignOut, isClientSwitching, isSigningOut } from 'src/hooks/clientTransition';
import {
  filterOpenOrganizations,
  isOrganizationPendingDeletion,
  pickOrganizationToRestore,
} from 'src/hooks/utils/organizationRestore';
import { getAdminPortalImpersonationReturnUrl, getAdminPortalUrl } from 'src/utils/adminPortalUtils';
import {
  ACCOUNT_SEPA_CONFIG_CACHE,
  LAST_ACTIVE_ORGANIZATION_CACHE,
  PASSWORD_MIGRATION_RESET_CACHE,
} from 'src/utils/cacheKeys';
import { readLastActiveOrganizationId, storeLastActiveOrganizationId } from 'src/utils/cacheUtils';
import { EMAIL_CHANGE_CALLBACK_PATH } from 'src/utils/emailChangeUtils';
import { PHONE_VERIFICATION_REQUIRED_CODE } from 'src/utils/phoneVerificationUtils';
import { slugifySpaceName } from 'src/utils/spaceUtils';

const getClientContactQuery = /* GraphQL */ `
  query GetClientContact {
    getClientContact {
      id
      firstName
      lastName
      commercialName
      companyName
      types
      phoneNumbers {
        number
      }
    }
  }
`;

interface ClientContactResult {
  getClientContact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    commercialName: string | null;
    companyName: string | null;
    types: string[] | null;
    phoneNumbers: PhoneNumber[] | null;
  } | null;
}

const getSendingEmailsQuery = /* GraphQL */ `
  query GetSendingEmails {
    getSendingEmails {
      chargeEmail
      issueEmail
      chargeEmailFromSendingSource
      issueEmailFromSendingSource
    }
  }
`;

interface SendingEmailsResult {
  getSendingEmails: {
    chargeEmail: string | null;
    issueEmail: string | null;
    chargeEmailFromSendingSource: string | null;
    issueEmailFromSendingSource: string | null;
  } | null;
}

export interface CreateWorkspaceParams {
  accountType: AccountType;
  companyName?: string;
  name: string;
  signupDetails: ClientSignupDetails;
}

export interface AcceptedInvitation {
  role: string | null;
  roleId: string | null;
}

export enum CreateWorkspaceStatus {
  CREATED = 'CREATED',
  CREATED_NOT_SWITCHED = 'CREATED_NOT_SWITCHED',
  FAILED = 'FAILED',
  PHONE_VERIFICATION_REQUIRED = 'PHONE_VERIFICATION_REQUIRED',
  RESTORED = 'RESTORED',
}

export enum LoginResult {
  SUCCESS = 'SUCCESS',
  TWO_FACTOR_REQUIRED = 'TWO_FACTOR_REQUIRED',
}

export interface TwoFactorSetupData {
  totpUri: string;
  backupCodes: string[];
}

export interface UserContext extends UserState {
  setTemporaryCredentials: (email: string, password: string | null) => void;
  login: (usernameOrEmail: string, password: string) => Promise<LoginResult>;
  verifyTwoFactor: (code: string, useBackupCode: boolean) => Promise<void>;
  enableTwoFactor: (password: string) => Promise<TwoFactorSetupData>;
  verifyTwoFactorSetup: (code: string) => Promise<void>;
  disableTwoFactor: (password: string) => Promise<void>;
  sendPhoneVerification: (phoneNumberToVerify: string) => Promise<void>;
  confirmPhoneVerification: (phoneNumberToVerify: string, code: string) => Promise<void>;
  fetchLinkedProviders: () => Promise<SocialProvider[]>;
  linkProvider: (provider: SocialProvider, returnUrl: string) => Promise<void>;
  unlinkProvider: (provider: SocialProvider) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (usernameOrEmail: string) => Promise<void>;
  forgotPasswordSubmit: (newPassword: string) => Promise<void>;
  setInitialPassword: (newPassword: string) => Promise<void>;
  signUp: (usernameOrEmail: string, password: string, name: string, familyName: string) => Promise<void>;
  joinSignUp: (usernameOrEmail: string, password: string, name: string, familyName: string) => Promise<void>;
  confirmSignUp: (usernameOrEmail: string, password: string) => Promise<void>;
  resendConfirmSignUp: (userNameOremail: string) => Promise<void>;
  cancelConfirmSignUp: () => void;
  createWorkspace: (parameters: CreateWorkspaceParams) => Promise<CreateWorkspaceStatus>;
  switchToSpace: (organizationId: string) => Promise<boolean>;
  acceptPendingInvitation: (invitation: PendingInvitationForCurrentUser) => Promise<AcceptedInvitation | null>;
  markActiveWorkspaceClosed: () => void;
  editUserPassword: (oldPassword: string, newPassword: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  updateUserEmail: (newEmail: string) => Promise<StatusResult>;
  refreshEmails: (issueEmailFromSendingSource: string, chargeEmailFromSendingSource: string) => void;
  setUserAttributes: (attributes: { [key: string]: string }) => Promise<void>;
  revalidateSession: () => void;
  stopImpersonating: () => Promise<void>;
  refreshActiveOrganization: () => Promise<void>;
}

export enum WorkspaceIssueKind {
  MULTIPLE_INVITATIONS = 'MULTIPLE_INVITATIONS',
  PENDING_INVITATION = 'PENDING_INVITATION',
  PROVISIONING_INCOMPLETE = 'PROVISIONING_INCOMPLETE',
  RESOLUTION_FAILED = 'RESOLUTION_FAILED',
  ACCOUNT_PENDING_DELETION = 'ACCOUNT_PENDING_DELETION',
  WORKSPACE_CLOSED = 'WORKSPACE_CLOSED',
}

export interface WorkspaceIssue {
  kind: WorkspaceIssueKind;
  organizationName: string | null;
  organizationId?: string | null;
  pendingDeletionAt?: DateTime | null;
  impersonatedBy?: string | null;
}

interface UserState {
  userAttributes: Record<string, string> | null;
  clientId: string | null;
  countryCode: CountryCode | null;
  countryName: string | null;
  countryDialCode: string | null;
  userId: string | null;
  groupIds: string[] | null;
  language: string | null;
  tenantId: string | null;
  isAloaltoDeveloper: boolean;
  impersonatedBy: string | null;
  twoFactorEnabled: boolean;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  isTenant: boolean;
  ownerId: string | null;
  isOwner: boolean;
  memberId: string | null;
  memberStatus: UserStatus | null;
  accountType: AccountType | null;
  rootUser: boolean;
  userRights: UserRight[] | null;
  features: string[];
  hasPassword: boolean | null;
  tempEmail: string | null;
  tempPass: string | null;
  isFetchingUser: boolean;
  accessRevoked: boolean;
  workspaceIssue: WorkspaceIssue | null;
  confirmSignUpChallenge: boolean;
  issueEmail: string | null;
  chargeEmail: string | null;
  issueEmailFromSendingSource: string | null;
  chargeEmailFromSendingSource: string | null;
  clientContactId: string | null;
  contact: {
    firstName: string | null;
    lastName: string | null;
    commercialName: string | null;
    companyName: string | null;
    types: ContactType[];
    phoneNumbers: PhoneNumber[];
  };
}

type Action =
  | {
      type: 'SET_USER';
      payload: {
        userAttributes: Record<string, string> | null;
        clientId: string | null;
        userId: string | null;
        groupIds: string[] | null;
        language: string | null;
        tenantId: string | null;
        ownerId: string | null;
        memberId: string | null;
        memberStatus: UserStatus | null;
        accountType: AccountType | null;
        rootUser: boolean;
        userRights: UserRight[] | null;
        features: string[];
        hasPassword: boolean | null;
        impersonatedBy: string | null;
        twoFactorEnabled: boolean;
        phoneNumber: string | null;
        phoneNumberVerified: boolean;
      };
    }
  | { type: 'SIGN_OUT' }
  | { type: 'SET_TEMPORARY_CREDENTIALS'; payload: { tempEmail: string; tempPass: string | null } }
  | {
      type: 'SET_LOCALIZATION';
      payload: { countryCode: CountryCode | null; countryName: string | null; countryDialCode: string | null };
    }
  | {
      type: 'SET_ISSUE_EMAIL';
      payload: {
        issueEmail?: string;
        chargeEmail?: string;
        issueEmailFromSendingSource: string;
        chargeEmailFromSendingSource: string;
      };
    }
  | {
      type: 'SET_CLIENT';
      payload: {
        issueEmail?: string;
        chargeEmail?: string;
        issueEmailFromSendingSource: string;
        chargeEmailFromSendingSource: string;
        clientContactId: string;
        contact: {
          firstName: string;
          lastName: string;
          commercialName: string;
          companyName: string;
          types: ContactType[];
          phoneNumbers: PhoneNumber[];
        };
      };
    }
  | { type: 'CONFIRM_SIGN_UP'; payload: { tempEmail: string; tempPass: string } }
  | { type: 'CANCEL_CONFIRM_SIGN_UP' }
  | { type: 'SET_WORKSPACE_ISSUE'; payload: { workspaceIssue: WorkspaceIssue } }
  | { type: 'ACCESS_REVOKED' };

const initialState: UserState = {
  userAttributes: null,
  clientId: null,
  language: null,
  userId: null,
  groupIds: null,
  tenantId: null,
  ownerId: null,
  memberId: null,
  memberStatus: null,
  accountType: null,
  rootUser: false,
  userRights: null,
  features: [],
  hasPassword: null,
  accessRevoked: false,
  workspaceIssue: null,
  confirmSignUpChallenge: false,
  countryCode: null,
  countryName: null,
  countryDialCode: null,
  isFetchingUser: true,
  isOwner: false,
  isTenant: false,
  isAloaltoDeveloper: false,
  impersonatedBy: null,
  twoFactorEnabled: false,
  phoneNumber: null,
  phoneNumberVerified: false,
  tempEmail: null,
  tempPass: null,
  issueEmail: null,
  chargeEmail: null,
  issueEmailFromSendingSource: null,
  chargeEmailFromSendingSource: null,
  clientContactId: null,
  contact: {
    firstName: null,
    lastName: null,
    commercialName: null,
    companyName: null,
    types: [],
    phoneNumbers: [],
  },
};

const parseMemberUserRights = (userRightsJson: string | null | undefined): UserRight[] | null => {
  if (isNil(userRightsJson)) {
    return null;
  }
  try {
    const parsed = JSON.parse(userRightsJson);
    return Array.isArray(parsed) ? (parsed as UserRight[]) : null;
  } catch {
    return null;
  }
};

const userReducer = (state: UserState, action: Action): UserState => {
  switch (action.type) {
    case 'SET_USER': {
      const currentUserDataFromState = pick(state, Object.keys(action.payload));
      if (isEqual(currentUserDataFromState, action.payload) && !state.isFetchingUser && isNil(state.workspaceIssue)) {
        return state;
      }
      const isOwner = !isNil(action.payload.ownerId);
      const isTenant = !isNil(action.payload.tenantId);
      const isAloaltoDeveloper =
        !isNil(action.payload.impersonatedBy) ||
        (!isTenant && (action.payload.userAttributes?.email?.endsWith('@aloalto.com') ?? false));

      return {
        ...state,
        isOwner,
        isAloaltoDeveloper,
        isTenant,
        ...action.payload,
        isFetchingUser: false,
        workspaceIssue: null,
      };
    }
    case 'SIGN_OUT': {
      const { tempEmail, tempPass, confirmSignUpChallenge } = state;
      return { ...initialState, isFetchingUser: false, tempEmail, tempPass, confirmSignUpChallenge };
    }
    case 'CONFIRM_SIGN_UP':
      return {
        ...state,
        tempEmail: action.payload.tempEmail,
        tempPass: action.payload.tempPass,
        confirmSignUpChallenge: true,
      };
    case 'CANCEL_CONFIRM_SIGN_UP':
      return { ...state, tempPass: null, confirmSignUpChallenge: false };
    case 'SET_WORKSPACE_ISSUE':
      if (isEqual(state.workspaceIssue, action.payload.workspaceIssue)) {
        return state;
      }
      return { ...state, workspaceIssue: action.payload.workspaceIssue, isFetchingUser: false };
    case 'ACCESS_REVOKED':
      if (state.accessRevoked) return state;
      return { ...state, accessRevoked: true };
    case 'SET_TEMPORARY_CREDENTIALS':
      if (state.tempEmail === action.payload.tempEmail || state.tempPass === action.payload.tempPass) {
        return state;
      }
      return { ...state, tempEmail: action.payload.tempEmail, tempPass: action.payload.tempPass };
    case 'SET_LOCALIZATION': {
      const { countryCode, countryName, countryDialCode } = state;
      if (isEqual({ countryCode, countryName, countryDialCode }, action.payload)) {
        return state;
      }
      return { ...state, ...action.payload };
    }
    case 'SET_ISSUE_EMAIL': {
      const emailState = {
        issueEmail: state.issueEmail,
        chargeEmail: state.chargeEmail,
        issueEmailFromSendingSource: state.issueEmailFromSendingSource,
        chargeEmailFromSendingSource: state.chargeEmailFromSendingSource,
      };
      if (isEqual(emailState, action.payload)) {
        return state;
      }
      return {
        ...state,
        issueEmail: action.payload.issueEmail ?? state.issueEmail,
        chargeEmail: action.payload.chargeEmail ?? state.chargeEmail,
        issueEmailFromSendingSource: action.payload.issueEmailFromSendingSource,
        chargeEmailFromSendingSource: action.payload.chargeEmailFromSendingSource,
      };
    }
    case 'SET_CLIENT':
      return {
        ...state,
        issueEmail: action.payload.issueEmail ?? state.issueEmail,
        chargeEmail: action.payload.chargeEmail ?? state.chargeEmail,
        issueEmailFromSendingSource: action.payload.issueEmailFromSendingSource,
        chargeEmailFromSendingSource: action.payload.chargeEmailFromSendingSource,
        clientContactId: action.payload.clientContactId,
        contact: {
          firstName: action.payload.contact.firstName,
          lastName: action.payload.contact.lastName,
          commercialName: action.payload.contact.commercialName,
          companyName: action.payload.contact.companyName,
          types: action.payload.contact.types,
          phoneNumbers: action.payload.contact.phoneNumbers,
        },
      };
    default:
      return state;
  }
};

// eslint-disable-next-line import/no-mutable-exports
export let initLanguage: string;
const browserLanguage = (navigator.language && navigator.languages[0]) || navigator.language || 'en-UK';
if (/^fr/.test(browserLanguage)) initLanguage = 'fr';
else if (/^nl/.test(browserLanguage)) initLanguage = 'nl';
else if (/^de/.test(browserLanguage)) initLanguage = 'de';
else initLanguage = 'en';

const UserContext = React.createContext<UserContext | null>(null);

const TOTP_IMAGE_URL = 'https://www.up2rent.com/wp-content/uploads/cropped-Favicon-192x192.png';
const TOTP_METHOD = 'totp';

const SESSION_REVALIDATION_THROTTLE_MS = 5_000; // parallel failing requests trigger one session refetch

// Reusing one token for its full lifetime lets the AppSync authorizer result
// cache (keyed by token) serve repeat requests instead of re-invoking the
// authorizer on every rotation
const authTokenCache = createAuthTokenCache(async () => {
  const { data, error } = await getAuthClientToken();
  return isNil(error) && !isNil(data) ? data.token : null;
});

export const getAuthToken = authTokenCache.getToken;

export const invalidateAuthTokenCache = authTokenCache.invalidate;

export const refreshAuthToken = authTokenCache.refreshToken;

const MAX_WORKSPACE_PROVISIONING_ATTEMPTS = 5;
const WORKSPACE_PROVISIONING_RETRY_DELAY = 1500;

const ORGANIZATION_ALREADY_EXISTS_ERROR_CODE = 'ORGANIZATION_ALREADY_EXISTS';
const MAX_ORGANIZATION_SLUG_ATTEMPTS = 5;
const ORGANIZATION_SLUG_SUFFIX_LENGTH = 8;

const createOrganizationWithUniqueSlug = async (parameters: Parameters<typeof createOrganization>[0]) => {
  const baseSlug = parameters.slug;
  let result = await createOrganization(parameters);
  let attempt = 1;
  while (result.error?.code === ORGANIZATION_ALREADY_EXISTS_ERROR_CODE && attempt < MAX_ORGANIZATION_SLUG_ATTEMPTS) {
    const slugSuffix = uuidv4().slice(0, ORGANIZATION_SLUG_SUFFIX_LENGTH);
    result = await createOrganization({ ...parameters, slug: `${baseSlug}-${slugSuffix}` });
    attempt += 1;
  }
  return result;
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<UserState, Action>>(userReducer, initialState);

  const orgRestoreAttemptedRef = useRef(false);
  const userHasOrgsRef = useRef<boolean | null>(null);
  const sessionUserIdRef = useRef<string | null>(null);
  const provisioningRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const provisioningAttemptsRef = useRef<number>(0);
  const sessionRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRetryAttemptsRef = useRef<number>(0);
  const workspaceRetryPendingRef = useRef<boolean>(false);
  const workspaceIssueRaisedRef = useRef<boolean>(false);
  const [provisioningAttempt, setProvisioningAttempt] = useState<number>(0);

  const session = useSession();
  const sessionResolution = resolveSessionQuery({
    data: session.data,
    error: session.error,
    isPending: session.isPending,
  });
  const { data: activeOrgData, isPending: activeOrgIsPending, refetch: refetchActiveOrg } = useActiveOrganization();

  useEffect(() => {
    sessionUserIdRef.current = session.data?.user?.id ?? null;
  }, [session.data]);

  const sessionRefetchRef = useRef(session.refetch);
  useEffect(() => {
    sessionRefetchRef.current = session.refetch;
  }, [session.refetch]);

  const activeOrgRefetchRef = useRef(refetchActiveOrg);
  useEffect(() => {
    activeOrgRefetchRef.current = refetchActiveOrg;
  }, [refetchActiveOrg]);

  const revocationCheckInProgressRef = useRef(false);
  const lastSessionRevalidationRef = useRef(0);

  const revalidateExpiredSession = () => {
    invalidateAuthTokenCache();
    const now = Date.now();
    if (now - lastSessionRevalidationRef.current < SESSION_REVALIDATION_THROTTLE_MS) {
      return;
    }
    lastSessionRevalidationRef.current = now;
    sessionRefetchRef.current();
  };

  const revalidateSessionNow = () => {
    invalidateAuthTokenCache();
    lastSessionRevalidationRef.current = Date.now();
    sessionRefetchRef.current();
    activeOrgRefetchRef.current?.();
    reconnectSubscriptions();
  };

  const scheduleSessionRetry = () => {
    if (!isNil(sessionRetryTimeoutRef.current)) {
      return;
    }
    const retryDelayMs = getSessionRetryDelayMs(sessionRetryAttemptsRef.current);
    sessionRetryAttemptsRef.current += 1;
    sessionRetryTimeoutRef.current = setTimeout(() => {
      sessionRetryTimeoutRef.current = null;
      sessionRefetchRef.current();
    }, retryDelayMs);
  };

  const refreshActiveOrganization = async () => {
    await activeOrgRefetchRef.current?.();
  };

  useEffect(() => {
    const userId = session.data?.user?.id;
    const organizationId = activeOrgData?.id;
    if (isNil(userId) || isNil(organizationId)) {
      return;
    }
    storeLastActiveOrganizationId(userId, organizationId);
  }, [session.data, activeOrgData]);

  useEffect(() => {
    return onUnauthorizedGraphqlRequest(() => {
      if (isClientSwitching() || isSigningOut()) return;
      if (isNil(sessionUserIdRef.current)) return;
      if (revocationCheckInProgressRef.current) return;
      revocationCheckInProgressRef.current = true;
      const deniedToken = authTokenCache.peekToken();
      void (async () => {
        try {
          invalidateAuthTokenCache();
          const { data, error } = await getSession();
          if (isClientSwitching() || isSigningOut()) return;
          if (!isNil(error)) {
            console.error('Could not confirm access revocation, treating as transient', error);
            return;
          }
          if (isNil(data?.user)) {
            revalidateExpiredSession();
            return;
          }
          const { data: activeOrganization } = await getFullOrganization();
          if (!isNil(activeOrganization) && isOrganizationPendingDeletion(activeOrganization)) {
            workspaceIssueRaisedRef.current = true;
            dispatch({
              type: 'SET_WORKSPACE_ISSUE',
              payload: {
                workspaceIssue: {
                  kind: WorkspaceIssueKind.WORKSPACE_CLOSED,
                  organizationId: activeOrganization.id,
                  organizationName: activeOrganization.name,
                  pendingDeletionAt: toDateTime(activeOrganization.pendingDeletionAt),
                },
              },
            });
            return;
          }
          const freshToken = await getAuthToken();
          switch (getAuthDenialOutcome(deniedToken, freshToken, activeOrganization?.id ?? null)) {
            case AuthDenialOutcome.STALE_TOKEN:
              window.location.reload();
              return;
            case AuthDenialOutcome.UNCONFIRMED:
              console.error('Could not confirm access revocation without the denied and a fresh token');
              return;
            default:
              dispatch({ type: 'ACCESS_REVOKED' });
          }
        } catch (error) {
          console.error('Could not confirm access revocation, treating as transient', error);
        } finally {
          revocationCheckInProgressRef.current = false;
        }
      })();
    });
  }, []);

  useEffect(() => {
    return onSessionExpiredGraphqlRequest(() => {
      if (isClientSwitching() || isSigningOut()) return;
      if (isNil(sessionUserIdRef.current)) return;
      revalidateExpiredSession();
    });
  }, []);

  useEffect(() => {
    let unmounted = false;

    const handlePendingInvitation = async (): Promise<boolean> => {
      if (window.location.pathname.startsWith(`${RouteDestination.ACCEPT_INVITATION}/`)) {
        return true;
      }
      const pendingInvitations = await fetchMyPendingInvitations();
      if (isEmpty(pendingInvitations)) {
        return false;
      }
      if (pendingInvitations.length > 1) {
        if (!unmounted) {
          workspaceIssueRaisedRef.current = true;
          dispatch({
            type: 'SET_WORKSPACE_ISSUE',
            payload: {
              workspaceIssue: { kind: WorkspaceIssueKind.MULTIPLE_INVITATIONS, organizationName: null },
            },
          });
        }
        return true;
      }
      const [invitationToAccept] = pendingInvitations;
      const { error: acceptationError } = await acceptInvitation({ invitationId: invitationToAccept.id });
      if (isNil(acceptationError)) {
        const { error: switchError } = await setActiveOrganization({
          organizationId: invitationToAccept.organizationId,
        });
        if (isNil(switchError)) {
          invalidateAuthTokenCache();
          refetchActiveOrg?.();
          return true;
        }
        console.error('Failed to activate the accepted invitation', switchError);
        if (!unmounted) {
          workspaceIssueRaisedRef.current = true;
          dispatch({
            type: 'SET_WORKSPACE_ISSUE',
            payload: {
              workspaceIssue: {
                kind: WorkspaceIssueKind.PENDING_INVITATION,
                organizationName: invitationToAccept.organizationName,
              },
            },
          });
        }
        return true;
      }
      console.error('Failed to accept the pending invitation', acceptationError);
      if (!unmounted) {
        workspaceIssueRaisedRef.current = true;
        dispatch({
          type: 'SET_WORKSPACE_ISSUE',
          payload: {
            workspaceIssue: {
              kind: WorkspaceIssueKind.PENDING_INVITATION,
              organizationName: invitationToAccept.organizationName,
            },
          },
        });
      }
      return true;
    };

    const resolveUser = async () => {
      if (sessionResolution === SessionQueryResolution.PENDING) {
        return;
      }
      if (sessionResolution === SessionQueryResolution.UNAVAILABLE) {
        scheduleSessionRetry();
        return;
      }
      sessionRetryAttemptsRef.current = 0;
      if (!session.data?.user) {
        orgRestoreAttemptedRef.current = false;
        userHasOrgsRef.current = null;
        provisioningAttemptsRef.current = 0;
        workspaceRetryPendingRef.current = false;
        workspaceIssueRaisedRef.current = false;
        setProvisioningAttempt(0);
        if (!unmounted) dispatch({ type: 'SIGN_OUT' });
        return;
      }

      const user = session.data.user;
      const org = activeOrgData;
      const impersonatedBy =
        (session.data.session as { impersonatedBy?: string | null } | undefined)?.impersonatedBy ?? null;

      if (user.role === 'admin' && isNil(impersonatedBy)) {
        window.location.replace(getAdminPortalUrl());
        return;
      }

      if (user.pendingDeletion) {
        if (!unmounted) {
          workspaceIssueRaisedRef.current = true;
          dispatch({
            type: 'SET_WORKSPACE_ISSUE',
            payload: {
              workspaceIssue: {
                kind: WorkspaceIssueKind.ACCOUNT_PENDING_DELETION,
                organizationName: null,
                pendingDeletionAt: toDateTime(user.pendingDeletionAt),
                impersonatedBy,
              },
            },
          });
        }
        return;
      }

      if (!isNil(org) && isOrganizationPendingDeletion(org)) {
        if (!unmounted) {
          workspaceIssueRaisedRef.current = true;
          dispatch({
            type: 'SET_WORKSPACE_ISSUE',
            payload: {
              workspaceIssue: {
                kind: WorkspaceIssueKind.WORKSPACE_CLOSED,
                organizationId: org.id,
                organizationName: org.name,
                pendingDeletionAt: toDateTime(org.pendingDeletionAt),
              },
            },
          });
        }
        return;
      }

      if (!isNil(org)) {
        workspaceIssueRaisedRef.current = false;
        orgRestoreAttemptedRef.current = false;
        provisioningAttemptsRef.current = 0;
      }

      if (!org && activeOrgIsPending) {
        return;
      }

      if (!org && workspaceRetryPendingRef.current) {
        return;
      }

      if (!org && workspaceIssueRaisedRef.current) {
        return;
      }

      if (!org && !orgRestoreAttemptedRef.current && !unmounted) {
        orgRestoreAttemptedRef.current = true;
        const canRetry = provisioningAttemptsRef.current < MAX_WORKSPACE_PROVISIONING_ATTEMPTS;
        const scheduleWorkspaceRetry = () => {
          provisioningAttemptsRef.current += 1;
          workspaceRetryPendingRef.current = true;
          provisioningRetryTimeoutRef.current = setTimeout(() => {
            workspaceRetryPendingRef.current = false;
            orgRestoreAttemptedRef.current = false;
            setProvisioningAttempt((attempt) => attempt + 1);
          }, WORKSPACE_PROVISIONING_RETRY_DELAY);
        };
        try {
          const { data: listedOrganizations, error: listError } = await listOrganizations();
          if (!isNil(listError)) {
            throw listError;
          }
          const organizations = filterOpenOrganizations(listedOrganizations ?? []);
          const organizationToRestore = pickOrganizationToRestore(organizations, readLastActiveOrganizationId(user.id));
          userHasOrgsRef.current = !isNil(organizationToRestore);
          if (!isNil(organizationToRestore)) {
            const { error: switchError } = await setActiveOrganization({
              organizationId: organizationToRestore.id,
            });
            if (!isNil(switchError)) {
              throw switchError;
            }
            invalidateAuthTokenCache();
            refetchActiveOrg?.();
            return;
          }
          if (!isEmpty(organizations) && !unmounted) {
            if (canRetry) {
              scheduleWorkspaceRetry();
              return;
            }
            workspaceIssueRaisedRef.current = true;
            dispatch({
              type: 'SET_WORKSPACE_ISSUE',
              payload: {
                workspaceIssue: {
                  kind: WorkspaceIssueKind.PROVISIONING_INCOMPLETE,
                  organizationName: organizations[0].name,
                },
              },
            });
            return;
          }
          if (isEmpty(organizations)) {
            const invitationHandled = await handlePendingInvitation();
            if (invitationHandled || unmounted) {
              return;
            }
          }
        } catch (error) {
          console.error('Failed to auto-restore active organization', error);
          if (canRetry && !unmounted) {
            scheduleWorkspaceRetry();
            return;
          }
          if (!unmounted) {
            workspaceIssueRaisedRef.current = true;
            dispatch({
              type: 'SET_WORKSPACE_ISSUE',
              payload: { workspaceIssue: { kind: WorkspaceIssueKind.RESOLUTION_FAILED, organizationName: null } },
            });
          }
          return;
        }
      }

      if (!org && userHasOrgsRef.current === true) {
        return;
      }

      // Workspace ID (was clientId)
      const clientId = org?.id ?? null;
      const language = user.language ?? initLanguage;

      let memberRole: string | null = null;
      let memberRootUser = false;
      let contactId: string | null = null;
      let memberDynamoUserId: string | null = null;
      let memberUserRights: UserRight[] | null = null;
      let memberStatus: UserStatus | null = null;
      const myMember = org?.members.find(
        (member: {
          userId: string;
          role: string;
          rootUser?: boolean | null;
          contactId?: string | null;
          dynamoUserId?: string | null;
          userRightsJson?: string | null;
          status?: string | null;
        }) => member.userId === user.id
      );
      if (myMember) {
        memberRole = myMember.role;
        memberRootUser = myMember.rootUser ?? false;
        contactId = myMember.contactId ?? null;
        memberDynamoUserId = myMember.dynamoUserId ?? null;
        memberUserRights = parseMemberUserRights(myMember.userRightsJson);
        memberStatus = (myMember.status as UserStatus | null) ?? null;
      }

      const rootUser = memberRootUser;
      const tenantId = memberRole === 'tenant' ? contactId : null;
      const ownerId = memberRole === 'owner' ? contactId : null;
      const memberId = contactId;
      const accountType = (org?.accountType as AccountType) ?? null;

      let hasPassword: boolean | null = null;
      let teamIds: string[] | null = null;
      let features: string[] = [];
      try {
        const { data: tokenData } = await getAuthClientToken();
        const fetchedToken = (tokenData as { token?: string } | null)?.token;
        if (fetchedToken) {
          const parts = fetchedToken.split('.');
          if (parts.length === 3) {
            try {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              if (typeof payload.hasPassword === 'boolean') hasPassword = payload.hasPassword;
              if (Array.isArray(payload.teamIds)) teamIds = payload.teamIds as string[];
              if (Array.isArray(payload.features)) features = payload.features as string[];
            } catch (decodeErr) {
              console.warn('Failed to decode JWT claims', decodeErr);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching auth token:', err);
      }

      const dynamoUserId: string | null = memberDynamoUserId ?? user.id;

      const userAttributes: Record<string, string> = {
        email: user.email,
        name: user.name,
        sub: user.id,
      };

      if (!unmounted) {
        dispatch({
          type: 'SET_USER',
          payload: {
            userAttributes,
            accountType,
            clientId,
            features,
            groupIds: teamIds,
            hasPassword,
            language,
            memberId,
            memberStatus,
            ownerId,
            rootUser,
            tenantId,
            userId: dynamoUserId,
            userRights: memberUserRights,
            impersonatedBy,
            twoFactorEnabled: (user as { twoFactorEnabled?: boolean | null }).twoFactorEnabled ?? false,
            phoneNumber: (user as { phoneNumber?: string | null }).phoneNumber ?? null,
            phoneNumberVerified: (user as { phoneNumberVerified?: boolean | null }).phoneNumberVerified ?? false,
          },
        });
      }
    };

    resolveUser();

    return () => {
      unmounted = true;
    };
  }, [session.data, sessionResolution, activeOrgData, activeOrgIsPending, refetchActiveOrg, provisioningAttempt]);

  useEffect(
    () => () => {
      if (!isNil(sessionRetryTimeoutRef.current)) {
        clearTimeout(sessionRetryTimeoutRef.current);
        sessionRetryTimeoutRef.current = null;
      }
      if (!isNil(provisioningRetryTimeoutRef.current)) {
        clearTimeout(provisioningRetryTimeoutRef.current);
        provisioningRetryTimeoutRef.current = null;
        workspaceRetryPendingRef.current = false;
      }
    },
    []
  );

  // Fetch country code
  useEffect(() => {
    let unmounted = false;
    const getCountryCode = async () => {
      try {
        const countryObject = await getCountryCodeAndNameOfClient();
        if (!unmounted) {
          dispatch({ type: 'SET_LOCALIZATION', payload: countryObject });
        }
      } catch (_err) {}
    };
    getCountryCode();
    return () => {
      unmounted = true;
    };
  }, []);

  // Fetch client email config when clientId changes
  useEffect(() => {
    let unmounted = false;
    const getClientDispatch = async () => {
      try {
        const [clientContactResult, sendingEmailsResult] = await Promise.all([
          graphqlClient.request<ClientContactResult>(getClientContactQuery),
          graphqlClient.request<SendingEmailsResult>(getSendingEmailsQuery),
        ]);
        const clientContact = clientContactResult.getClientContact;
        const sendingEmails = sendingEmailsResult.getSendingEmails;
        if (!unmounted && clientContact) {
          dispatch({
            type: 'SET_CLIENT',
            payload: {
              issueEmail: sendingEmails?.issueEmail ?? '',
              chargeEmail: sendingEmails?.chargeEmail ?? '',
              issueEmailFromSendingSource: sendingEmails?.issueEmailFromSendingSource ?? '',
              chargeEmailFromSendingSource: sendingEmails?.chargeEmailFromSendingSource ?? '',
              clientContactId: clientContact.id,
              contact: {
                firstName: clientContact.firstName ?? '',
                lastName: clientContact.lastName ?? '',
                commercialName: clientContact.commercialName ?? '',
                companyName: clientContact.companyName ?? '',
                types: (clientContact.types as ContactType[]) ?? [],
                phoneNumbers: clientContact.phoneNumbers ?? [],
              },
            },
          });
        }
      } catch (err) {
        console.error('Error getting Emails:', err);
      }
    };
    if (state.clientId) {
      getClientDispatch();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.clientId]);

  const login = async (usernameOrEmail: string, password: string): Promise<LoginResult> => {
    const { data, error } = await signInWithEmail({
      email: usernameOrEmail,
      password,
    });
    if (error) {
      throw Object.assign(new Error(error.message ?? 'Sign in failed'), { code: error.code });
    }
    if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
      return LoginResult.TWO_FACTOR_REQUIRED;
    }
    invalidateAuthTokenCache();
    return LoginResult.SUCCESS;
  };

  const verifyTwoFactor = async (code: string, useBackupCode: boolean): Promise<void> => {
    const { error } = useBackupCode ? await verifyBackupCode({ code }) : await verifyTotp({ code });
    if (error) {
      throw new Error(error.message ?? 'Invalid code');
    }
    invalidateAuthTokenCache();
    session.refetch();
  };

  const enableTwoFactor = async (password: string): Promise<TwoFactorSetupData> => {
    const { data, error } = await enableTwoFactorRequest({ password, method: TOTP_METHOD });
    if (error || isNil(data) || data.method !== TOTP_METHOD) {
      throw new Error(error?.message ?? 'Could not enable two-factor authentication');
    }
    return {
      totpUri: `${data.totpURI}&image=${encodeURIComponent(TOTP_IMAGE_URL)}`,
      backupCodes: data.backupCodes,
    };
  };

  const verifyTwoFactorSetup = async (code: string): Promise<void> => {
    const { error } = await verifyTotp({ code });
    if (error) {
      const verifyError = new Error(error.message ?? 'Invalid code');
      verifyError.name = error.code ?? 'TwoFactorVerifyError';
      throw verifyError;
    }
    session.refetch();
  };

  const disableTwoFactor = async (password: string): Promise<void> => {
    const { error } = await disableTwoFactorRequest({ password });
    if (error) {
      throw new Error(error.message ?? 'Could not disable two-factor authentication');
    }
    session.refetch();
  };

  const sendPhoneVerification = async (phoneNumberToVerify: string): Promise<void> => {
    const { error } = await sendPhoneVerificationOtp({ phoneNumber: phoneNumberToVerify });
    if (error) {
      throw new PhoneVerificationError(error.message ?? 'Could not send the verification code', error.code);
    }
  };

  const confirmPhoneVerification = async (phoneNumberToVerify: string, code: string): Promise<void> => {
    const { error } = await verifyPhoneNumberOtp({ phoneNumber: phoneNumberToVerify, code });
    if (error) {
      throw new PhoneVerificationError(error.message ?? 'Invalid code', error.code);
    }
    session.refetch();
  };

  const fetchLinkedProviders = async (): Promise<SocialProvider[]> => {
    const { data, error } = await listLinkedAccounts();
    if (error || isNil(data)) {
      throw new Error(error?.message ?? 'Could not load the linked sign-in methods');
    }
    return getLinkedProviders(data);
  };

  const linkProvider = async (provider: SocialProvider, returnUrl: string): Promise<void> => {
    const { error } = await linkSocialAccount({ provider, callbackURL: returnUrl, errorCallbackURL: returnUrl });
    if (error) {
      throw new Error(error.message ?? 'Could not link the sign-in method');
    }
  };

  const unlinkProvider = async (provider: SocialProvider): Promise<void> => {
    const { data: accounts, error: listError } = await listLinkedAccounts();
    if (listError || isNil(accounts)) {
      throw new Error(listError?.message ?? 'Could not load the linked sign-in methods');
    }
    // One at a time: Better Auth refuses to unlink a user's last account.
    for (const accountId of getProviderAccountIds(accounts, provider)) {
      const { error } = await unlinkSocialAccount({ accountId });
      if (error) {
        const unlinkError = new Error(error.message ?? 'Could not unlink the sign-in method');
        unlinkError.name = error.code ?? unlinkError.name;
        throw unlinkError;
      }
    }
  };

  const logout = async () => {
    beginSignOut();
    try {
      await signOut();
    } catch (error) {
      endSignOut();
      throw error;
    }
    // Drop the JWT cache so the next user doesn't inherit a stale token.
    invalidateAuthTokenCache();
    // Clear local storage
    const itemsToRemoveFromLocalStorage: string[] = [];
    for (const localSave in window.localStorage) {
      if (
        !localSave.endsWith(ACCOUNT_SEPA_CONFIG_CACHE) &&
        !localSave.startsWith(LAST_ACTIVE_ORGANIZATION_CACHE) &&
        localSave !== PASSWORD_MIGRATION_RESET_CACHE
      ) {
        itemsToRemoveFromLocalStorage.push(localSave);
      }
    }
    itemsToRemoveFromLocalStorage.forEach((itemToRemove) => window.localStorage.removeItem(itemToRemove));
    window.location.replace(RouteDestination.ROOT);
  };

  const forgotPassword = async (usernameOrEmail: string) => {
    await requestPasswordReset({
      email: usernameOrEmail,
      redirectTo: `${window.location.origin}${RouteDestination.RESET_PASSWORD}`,
    });
  };

  const forgotPasswordSubmit = async (newPassword: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (isNil(token)) {
      throw new Error('Password reset token not found in URL');
    }
    const { error } = await resetPassword({
      newPassword,
      token,
    });
    if (error) {
      throw new Error(error.message ?? 'Could not reset password, please try again.');
    }
  };

  const setInitialPassword = async (newPassword: string) => {
    const { error } = await setPassword({ newPassword });
    if (error) {
      throw new Error(error.message ?? 'Could not set password, please try again.');
    }
    invalidateAuthTokenCache();
  };

  const setTemporaryCredentials = (email: string, password: string | null) => {
    dispatch({ type: 'SET_TEMPORARY_CREDENTIALS', payload: { tempEmail: email, tempPass: password } });
  };

  const signUp = async (usernameOrEmail: string, password: string, firstName: string, lastName: string) => {
    const { error } = await signUpWithEmail({
      email: usernameOrEmail,
      password,
      name: `${firstName} ${lastName}`,
      firstName,
      lastName,
      language: initLanguage,
      callbackURL: window.location.origin,
    });
    if (error) {
      throw new Error(error.message ?? 'Sign up failed');
    }
    dispatch({ type: 'CONFIRM_SIGN_UP', payload: { tempEmail: usernameOrEmail, tempPass: password } });
  };

  const joinSignUp = (usernameOrEmail: string, password: string, firstName: string, lastName: string) =>
    signUp(usernameOrEmail, password, firstName, lastName);

  const confirmSignUp = async (usernameOrEmail: string, password: string) => {
    await login(usernameOrEmail, password);
  };

  const resendConfirmSignUp = async (userNameOrEmail: string) => {
    await sendVerificationEmail({
      email: userNameOrEmail,
      callbackURL: window.location.origin,
    });
  };

  const cancelConfirmSignUp = () => {
    dispatch({ type: 'CANCEL_CONFIRM_SIGN_UP' });
  };

  const createWorkspace = async ({
    accountType,
    companyName,
    name,
    signupDetails,
  }: CreateWorkspaceParams): Promise<CreateWorkspaceStatus> => {
    if (isNil(state.clientId)) {
      const { data: existingOrganizations, error: listError } = await listOrganizations();
      if (!isNil(listError)) {
        return CreateWorkspaceStatus.FAILED;
      }
      const organizationToRestore = pickOrganizationToRestore(existingOrganizations ?? [], null);
      if (!isNil(organizationToRestore)) {
        const { error: restoreError } = await setActiveOrganization({ organizationId: organizationToRestore.id });
        if (!isNil(restoreError)) {
          return CreateWorkspaceStatus.FAILED;
        }
        invalidateAuthTokenCache();
        session.refetch();
        return CreateWorkspaceStatus.RESTORED;
      }
    }
    const { data: createdOrganization, error: createError } = await createOrganizationWithUniqueSlug({
      accountType,
      companyName,
      name,
      slug: slugifySpaceName(name),
      metadata: { signupDetails },
    });
    if (!isNil(createError) || isNil(createdOrganization)) {
      if (createError?.code === PHONE_VERIFICATION_REQUIRED_CODE) {
        return CreateWorkspaceStatus.PHONE_VERIFICATION_REQUIRED;
      }
      console.error('[UserContext] createWorkspace failed', createError?.message);
      return CreateWorkspaceStatus.FAILED;
    }
    const { error: switchError } = await setActiveOrganization({ organizationId: createdOrganization.id });
    if (!isNil(switchError)) {
      return CreateWorkspaceStatus.CREATED_NOT_SWITCHED;
    }
    invalidateAuthTokenCache();
    session.refetch();
    refetchActiveOrg?.();
    return CreateWorkspaceStatus.CREATED;
  };

  const switchToSpace = async (organizationId: string): Promise<boolean> => {
    const { error } = await setActiveOrganization({ organizationId });
    if (!isNil(error)) {
      return false;
    }
    invalidateAuthTokenCache();
    return true;
  };

  const acceptPendingInvitation = async (
    invitation: PendingInvitationForCurrentUser
  ): Promise<AcceptedInvitation | null> => {
    const { data, error } = await acceptInvitation({ invitationId: invitation.id });
    if (!isNil(error) || isNil(data)) {
      console.error('Failed to accept the invitation from the space switcher', error);
      return null;
    }
    const { error: switchError } = await setActiveOrganization({ organizationId: invitation.organizationId });
    if (!isNil(switchError)) {
      console.error('Failed to activate the accepted invitation', switchError);
      return null;
    }
    invalidateAuthTokenCache();
    return { role: data.invitation?.role ?? null, roleId: data.invitation?.roleId ?? null };
  };

  const markActiveWorkspaceClosed = () => {
    workspaceIssueRaisedRef.current = true;
    dispatch({
      type: 'SET_WORKSPACE_ISSUE',
      payload: {
        workspaceIssue: {
          kind: WorkspaceIssueKind.WORKSPACE_CLOSED,
          organizationId: activeOrgData?.id ?? state.clientId,
          organizationName: activeOrgData?.name ?? null,
          pendingDeletionAt: todayDateTime(),
        },
      },
    });
  };

  const editUserPassword = async (oldPassword: string, newPassword: string) => {
    const { error } = await changePassword({
      currentPassword: oldPassword,
      newPassword,
    });
    if (error) {
      const passwordError = new Error(error.message ?? 'Password change failed');
      passwordError.name = error.code ?? 'PasswordChangeError';
      throw passwordError;
    }
  };

  const refreshToken = async (): Promise<void> => {
    session.refetch();
  };

  const stopImpersonating = async (): Promise<void> => {
    await stopImpersonationRequest();
    invalidateAuthTokenCache();
    window.location.assign(getAdminPortalImpersonationReturnUrl());
  };

  const refreshEmails = (issueEmailFromSendingSource: string, chargeEmailFromSendingSource: string): void => {
    dispatch({
      type: 'SET_ISSUE_EMAIL',
      payload: { issueEmailFromSendingSource, chargeEmailFromSendingSource },
    });
  };

  const updateUserEmail = async (newEmail: string): Promise<StatusResult> => {
    const { error } = await changeEmail({
      newEmail,
      callbackURL: buildEmailChangeCallbackUrl(window.location.origin, EMAIL_CHANGE_CALLBACK_PATH),
    });
    if (error) {
      return {
        __typename: 'StatusResult',
        success: false,
        message: error.code ?? error.message ?? 'Failed to change email',
      };
    }
    return { __typename: 'StatusResult', success: true };
  };

  const setUserAttributes = async (attributes: { [key: string]: string }) => {
    const { error } = await updateUser(attributes);
    if (error) {
      throw new Error(error.message ?? 'Failed to update user attributes');
    }
    session.refetch();
  };

  const values = React.useMemo(
    () => ({
      ...state,
      setTemporaryCredentials,
      login,
      verifyTwoFactor,
      enableTwoFactor,
      verifyTwoFactorSetup,
      disableTwoFactor,
      sendPhoneVerification,
      confirmPhoneVerification,
      fetchLinkedProviders,
      linkProvider,
      unlinkProvider,
      logout,
      forgotPassword,
      forgotPasswordSubmit,
      setInitialPassword,
      signUp,
      joinSignUp,
      confirmSignUp,
      resendConfirmSignUp,
      cancelConfirmSignUp,
      createWorkspace,
      switchToSpace,
      acceptPendingInvitation,
      markActiveWorkspaceClosed,
      editUserPassword,
      updateUserEmail,
      refreshToken,
      refreshEmails,
      setUserAttributes,
      revalidateSession: revalidateSessionNow,
      stopImpersonating,
      refreshActiveOrganization,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContext => {
  const context = React.useContext(UserContext);

  if (context === undefined) {
    throw new Error('`useUser` hook must be used within a `UserProvider` component');
  }
  return context as UserContext;
};

export default UserContext;
